<template>
  <v-app dark>
    <div v-if="routesLoading" class="loading-wrapper">
      <div class="loading-img">
        <div id="loader" aria-valuetext="loading"></div>
      </div>
    </div>
    <v-overlay v-if="!online" z-index="300">
      <v-card light>
        <v-toolbar color="secondary" elevation="0">
          <v-toolbar-title>
            <v-icon slot="icon" class="mr-2">
              mdi-wifi-strength-alert-outline
            </v-icon>
            Network Error
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <p>Unable to connect to the PlatformGold® Trading Server.</p>
          <p>
            Please try checking your internet connection, reconnecting to Wi-Fi,
            refreshing your browser, and restarting your device.
          </p>
          <p>To confirm an order by phone, call 1-315-637-1300.</p>
        </v-card-text>
      </v-card>
    </v-overlay>
    <router-view v-if="forceUpdate"></router-view>
    <v-footer v-if="!routesLoading" padless>
      <footer-content></footer-content>
    </v-footer>
    <!-- global snackbar -->
    <v-snackbar
      :timeout="snackbar.timeout || 20000"
      app
      top
      centered
      :color="snackbar.color"
      v-model="snackbar.show"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          v-if="snackbar.linkToTradeTicket"
          text
          v-bind="attrs"
          @click=";(snackbar.show = false), (showTradeTicket = true)"
        >View Trade Ticket
        </v-btn>
        <v-btn
          v-if="snackbar.linkToOrder"
          text
          v-bind="attrs"
          @click=";(snackbar.show = false), goToOrder(snackbar.linkToOrder)"
        >View Order
        </v-btn>
        <v-btn icon v-bind="attrs" @click="snackbar.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <!-- global communication preferences -->
    <v-dialog v-model="showCommunicationPreferences" max-width="600">
      <v-card>
        <communication-preferences
          :shown="showCommunicationPreferences"
          :alertBanner="communicationPreferencesText"
        />
      </v-card>
    </v-dialog>
    <!-- global trade ticket -->
    <v-dialog
      v-model="showTradeTicket"
      persistent
      fullscreen
      hide-overlay
      :transition="false"
    >
      <v-card>
        <trade-ticket
          :shown="showTradeTicket"
          :defaultBuySell="defaultBuySell"
          v-on:close="showTradeTicket = false"
        ></trade-ticket>
      </v-card>
    </v-dialog>
    <!-- global limit order -->
    <v-dialog v-model="showLimitOrder" persistent fullscreen hide-overlay>
      <v-card>
        <limit-order
          v-if="showLimitOrder"
          :shown="showLimitOrder"
          :selectedProduct="selectedProduct"
          :buySell="buySell"
          :quantity="quantity"
          v-on:close="showLimitOrder = false"
        ></limit-order>
      </v-card>
    </v-dialog>
    <!-- global call log modal for select users -->
    <v-dialog v-if="enableCallLogWidget" v-model="showCallLog" max-width="600">
      <log-phone-call v-if="showCallLog" :phone-number="phoneNumber" v-on:close="showCallLog = false"></log-phone-call>
    </v-dialog>
    <!-- global add to trade ticket -->
    <v-dialog v-model="showAddToTradeTicket" width="600">
      <v-card>
        <add-to-trade-ticket
          v-if="showAddToTradeTicket"
          :shown="showAddToTradeTicket"
          :selectedProduct="selectedProduct"
          :buySell="buySell"
          :quantity="quantity"
          v-on:close="showAddToTradeTicket = false"
        ></add-to-trade-ticket>
      </v-card>
    </v-dialog>
    <!-- global stock alert added -->
    <v-dialog v-model="showStockAlertAdded" max-width="600">
      <stock-alert-added
        :selectedProduct="selectedProduct"
        :buySell="buySell"
        :text="text"
        v-on:close="showStockAlertAdded = false"
      ></stock-alert-added>
    </v-dialog>
    <!-- global confirm dialog -->
    <confirm ref="confirm"></confirm>
    <!-- global reminder dialog -->
    <v-dialog v-model="activeReminders" max-width="800" persistent>
      <reminder-carousel :active-reminder-ids="getActiveReminderIds"></reminder-carousel>
    </v-dialog>
  </v-app>
</template>

<script>
import TradeTicket from '@/components/cart/TradeTicket'
import Confirm from '@/components/Confirm'
import _ from 'lodash'
import AddToTradeTicket from '@/components/cart/AddToTradeTicket'
import Vue from 'vue'
import FooterContent from '@/components/FooterContent'
import CommunicationPreferences from '@/components/user/CommunicationPreferences'
import LimitOrder from '@/components/cart/LimitOrder'
import StockAlertAdded from '@/components/cart/StockAlertAdded'
import ReminderCarousel from "@/components/user/ReminderCarousel";
import loggerMixin from '@/mixins/logger';
import Util from '@/util';
import {mapGetters} from "vuex";
import { setOptions as gTagSetOptions, bootstrap as gTagBootstrap } from 'vue-gtag'
import LogPhoneCall from '@/components/widgets/LogPhoneCall';
import {loadRingCentralSDK} from '@/util/ringcentral';

export default {
  computed: {
    ...mapGetters([
      'getActiveReminderIds',
      'getSelectedCompany',
      'getUserProfile'
    ]),
    activeReminders() {
      return this.getSelectedCompany.active && this.getActiveReminderIds.length > 0
    }
  },
  components: {
    LogPhoneCall,
    ReminderCarousel,
    StockAlertAdded,
    LimitOrder,
    CommunicationPreferences,
    FooterContent,
    AddToTradeTicket,
    TradeTicket,
    Confirm,
  },
  data() {
    return {
      showCommunicationPreferences: false,
      communicationPreferencesText: ``,
      forceUpdate: true,
      online: true,
      showAddToTradeTicket: false,
      showTradeTicket: false,
      showLimitOrder: false,
      enableCallLogWidget: false,
      showCallLog: false,
      phoneNumber: null,
      showStockAlertAdded: false,
      text: null,
      defaultBuySell: null,
      rightDrawer: false,
      snackbar: {
        show: false,
        text: '',
        color: '',
      },
      selectedProduct: null,
      buySell: null,
      quantity: null,
      routesLoading: true,
    }
  },
  methods: {
    goToOrder({type, id}) {
      const path = type === `so` ? `sales-orders` : `purchase-orders`
      this.$router.push(`/orders/${path}/${id}`)
    },
    formatSnackbarText(text) {
      if (typeof text !== 'string') return text
      const trimmed = text.trim()
      if (trimmed.length === 0) return text
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    },
    enableGoogleAnalytics() {
      // Default to develop tag. Important to have a default because otherwise errors will be thrown throughout the app.
      const gTagId = (process.env.VUE_APP_GTAG_ID || 'G-1GM6R2GP7Y').trim()
      const debug_mode = (process.env.VUE_APP_GTAG_DEBUG || 'false').trim().toLowerCase() === 'true'
      gTagSetOptions({
        config: {
          id: gTagId,
          params: {
            debug_mode,
            send_page_view: true,
          }
        },
      })
      gTagBootstrap()
    }
  },
  mixins: [loggerMixin],
  async mounted() {
    if (typeof window !== undefined && window._VMA === undefined) {
      window._VMA = this
    }
    this.enableGoogleAnalytics()
    if (this.getUserProfile.isAdmin && this.getUserProfile.accessCallLogWidget) {
      await loadRingCentralSDK()
      this.enableCallLogWidget = true
      window.addEventListener('message', (e) => {
        const data = e.data;
        if (data) {
          switch (data.type) {
            case 'rc-call-ring-notify':
              // Inbound calls: phone rings when customer calls trader
              if (data.call?.direction === 'Outbound' && this.showCallLog) break;
              this.showCallLog = true
              this.phoneNumber = data.call?.direction === 'Inbound' ? data.call?.from : data.call?.to
              break;
            case 'rc-active-call-notify':
              // Outbound calls: show modal when call starts ringing
              if (data.call?.direction === 'Outbound' && data.call?.telephonyStatus === 'Ringing' && !this.showCallLog) {
                this.showCallLog = true
                this.phoneNumber = data.call?.to?.phoneNumber
              }
              break;
            default:
              break;
          }
        }
      });
    }
  },
  created() {
    this.$on('SHOW_CALL_LOG_WIDGET', async () => {
      this.showCallLog = true
    })
    this.$on('ONLINE', async () => {
      this.forceUpdate = false
      await Vue.nextTick()
      this.online = true
      this.forceUpdate = true
    })
    this.$on('FORCE_UPDATE', async () => {
      this.forceUpdate = false
      await Vue.nextTick()
      this.forceUpdate = true
    })
    this.$on('OFFLINE', () => {
      this.online = false
    })
    this.$on(
      'CONFIRM',
      async ({
               title = '',
               message = '',
               options = {},
               callback = () => {
               },
             }) => {
        if (await this.$refs.confirm.open(title, message, options)) {
          await callback(true)
        } else {
          await callback(false)
        }
      }
    )
    this.$on('LOGOUT', () => {
      this.$store.resetState()
      window.location.assign('/#/auth/login')
    })
    this.$on('OPEN_COMMUNICATION_PREFERENCES', (params) => {
      this.showCommunicationPreferences = true
      this.communicationPreferencesText = _.get(params, `text`)
    })
    this.$on('CLOSE_COMMUNICATION_PREFERENCES', () => {
      this.showCommunicationPreferences = false
    })
    this.$on('SHOW_TRADE_TICKET', (e) => {
      if (e !== undefined && e.defaultBuySell !== undefined)
        this.defaultBuySell = e.defaultBuySell
      this.showTradeTicket = true
    })
    this.$on('SHOW_LIMIT_ORDER', (e) => {
      this.selectedProduct = e.selectedProduct
      this.buySell = e.buySell
      this.quantity = e.quantity
      this.showLimitOrder = true
    })
    this.$on('SHOW_ADD_TO_TRADE_TICKET', (e) => {
      this.selectedProduct = e.selectedProduct
      this.buySell = e.buySell
      this.quantity = e.quantity
      this.showAddToTradeTicket = true
    })
    this.$on('SHOW_STOCK_ALERT_ADDED', (e) => {
      this.selectedProduct = e.selectedProduct
      this.buySell = e.buySell
      this.text = e.text
      this.showStockAlertAdded = true
    })
    this.$on('SHOW_SNACKBAR', async (e) => {
      if (e.text.match(/(quota(.*exceed|.*full)|storage.*(full|exceed)|Exceeded.*(storage|quota)|The quota has been exceeded|DOMException.*22)/i) !== null) {
        this.snackbar = {
          show: true,
          text: 'Platform Gold is running out of memory. Please clear your browser cache, log out, and login again.',
          type: 'error',
        }
        this.log({
          message: `Storage Quota Exceeded Error: ${e.text}`,
          deviceInfo: {
            ...Util.getDeviceAndBrowserInfo(),
            ...await Util.getStorageUsageInfo(),
          },
          isError: true,
          success: false,
        })
        return
      }
      this.snackbar = {
        show: true,
        text: (e && e.preserveCase) ? e.text : this.formatSnackbarText(e.text),
        color: e.color,
        timeout: e.timeout || 20000,
        linkToTradeTicket: _.get(e, 'linkToTradeTicket', false),
        linkToOrder: _.get(e, 'linkToOrder', false),
      }
    })
    this.$on('AUTH_FAILED', () => {
      this.$store.commit('SET_ACCESS_TOKEN', null)
      this.$router.push({
        path: '/auth/login',
        query: {
          redirect:
            this.$route.name !== 'login' ? this.$route.path : `/dashboard`,
        },
      })
    })
    this.$on('DISMISS_SNACKBAR', () => {
      this.snackbar.show = false
    })
    this.$on('SERVER_ERROR', () => {
      this.snackbar = {
        show: true,
        text: 'Server Error',
        color: 'error',
      }
    })
    this.$on('TOO_MANY_REQUESTS', ({data}) => {
      this.snackbar = {
        show: true,
        text: data,
        color: 'error',
      }
    })

    this.$router.beforeEach((_to, _from, next) => {
      this.routesLoading = true;
      next();
    });

    this.$router.afterEach(() => {
      this.routesLoading = false;
    });
  },
}
</script>

<style lang="sass">
/**
 * Add spacing for system bar (ticker) at different breakpoints.
 */
.v-dialog--fullscreen
  top: 40px
// Match to height() in Ticker/top() in AppToolbar
.theme--light.v-application
  background-color: #EFEFF4

.v-data-table th
  white-space: nowrap

.loading-wrapper
  display: flex
  flex-direction: column
  height: 100vh
  width: 100vw
  justify-content: center
  align-items: center
  background: linear-gradient(to bottom, #000000 50%, #f8f8f8 50%)
  position: relative
  overflow: hidden

#loader
  position: absolute
  top: 0
  left: 0
  width: 100%
  height: 100%
  border: 4px solid rgba(0, 0, 0, 0.1)
  border-radius: 50% !important
  border-top: 4px solid #cb972c
  animation: spin 1s linear infinite
  background-color: transparent

.loading-img
  position: relative
  width: 10vw
  height: 10vw
  border-radius: 50% !important
  background: url('../public/ucg_logo_small.png') no-repeat center center
  background-size: contain

@keyframes spin
  0%
    transform: rotate(0deg)
  100%
    transform: rotate(360deg)

// Global style for standard input labels (TextField, TextArea, Select, Autocomplete, Combobox)
::v-deep .v-text-field:not(.v-input--dense) .v-label,
::v-deep .v-textarea:not(.v-input--dense) .v-label,
::v-deep .v-select:not(.v-input--dense) .v-label,
::v-deep .v-autocomplete:not(.v-input--dense) .v-label,
::v-deep .v-combobox:not(.v-input--dense) .v-label
  font-size: 1rem !important
  opacity: 1

// Global style specifically for dense input labels
::v-deep .v-input--dense .v-label
  font-size: 1rem !important
  opacity: 1
</style>

<style>
* {
  border-radius: 0 !important;
}
</style>